import { EventEmitter, Injectable, Output } from '@angular/core';
import { AbstractControl } from '@angular/forms';
import { LabConfigurationService } from '../data-service/lab-configuration.service';
import { ScanStatus } from '../enums/scan-status.enum';
import { AdditionalField } from '../models/additional-field.model';
import { CurrentOrder } from '../models/current-order.model';
import { FormatterHelperService } from './formatter-helper.service';
import { LabType } from '../enums/lab-type.enum';
import { LabsiteCheckService } from '../labsite-check/labsite-check.service';

@Injectable({
  providedIn: 'root'
})
export class CurrentOrderService {
  currentOrder: CurrentOrder;
  isSomeOrAllInvoiceableItemsAreRemoved = false;
  @Output() sampleLevelFileStatusChanged = new EventEmitter<{ code: string, internalFileName: string, scanStatus: ScanStatus }>();
  @Output() orderLevelFileStatusChanged = new EventEmitter<{ internalFileName: string, scanStatus: ScanStatus }>();
  @Output() attachmentFileStatusChanged = new EventEmitter<{ internalFileName: string, scanStatus: ScanStatus }>();
  @Output() favoriteSampleDeletedEvent = new EventEmitter<{ code: string }>();
  @Output() analysesRemovedEvent = new EventEmitter<{code: string}>();
  
  async isExternalLIMS(): Promise<boolean> {
    var isElims = await this.labsiteCheckService.isElimsOrTransitionedToElims(this.currentOrder?.quotation?.receivingLabCode, this.currentOrder?.labDetails?.type,
      this.currentOrder?.account?.code);
    return !isElims;
  }
  isElimsLab = false;
  constructor(
    private labConfigQryClient: LabConfigurationService,
    private formatterHelperService: FormatterHelperService,
    private labsiteCheckService: LabsiteCheckService
  ) { }

  sortAllAdditionalfieldForReviewAndConfirmPage() {
    if (this.currentOrder) {
      if (this.currentOrder.samples && this.currentOrder.samples.length > 0) {
        this.currentOrder.samples.forEach(x => {
          if (x.additionalFields && x.additionalFields.length > 0) {
            x.additionalFields = x.additionalFields.sort((a, b) => {
              if (a.isMandatory === b.isMandatory) {
                return 0; 
              }
              if (a.isMandatory) {
                return -1; 
              }
              return 1;
            });
            x.displayAdditionalFields = x.additionalFields;
          }
        });
      }

      if (this.currentOrder.additionalFields && this.currentOrder.additionalFields.length > 0) {
        this.currentOrder.displayAdditionalFields = this.currentOrder.additionalFields;
      }
    }
  }

  updateFileStatus(internalFileName: string, scanStatus: ScanStatus) {
    const nameArray = internalFileName.split('/');
    const sampleReference = nameArray.length === 6 ? nameArray[4] : '';

    if (sampleReference && sampleReference !== '') {
      // sample case
      const sample = this.currentOrder.samples.find(x => x.eurofinsSampleCode === sampleReference);
      if (sample) {
        const doc = sample.attachDocuments.find(x => x.internalFileName === internalFileName);
        if (doc) {
          doc.status = scanStatus;
          this.sampleLevelFileStatusChanged.emit({ code: sampleReference, internalFileName, scanStatus });
        }
      }
    } else {
      // else order
      const doc = this.currentOrder.attachDocuments?.find(x => x.internalFileName === internalFileName);
      if (doc) {
        doc.status = scanStatus;
        this.orderLevelFileStatusChanged.emit({ internalFileName, scanStatus });
      }
    }
    this.attachmentFileStatusChanged.emit({ internalFileName, scanStatus });
  }

  updateSampleFromFavoriteSampleIcon(code: string) {
    this.favoriteSampleDeletedEvent.emit({ code });
  }

  updateCountOfAnalyses(){
    this.analysesRemovedEvent.emit();
  }

  SortAdditionalFieldsFormArray(abstractControls: AbstractControl[]): AbstractControl[] {
    const sampleServiceMandatory: AbstractControl[] = [];
    const sampleServiceOptional: AbstractControl[] = [];
    const orderServiceMandatory: AbstractControl[] = [];
    const orderServiceOptional: AbstractControl[] = [];

    for (const control of abstractControls) {
      if (control.value.scope === 'Sample') {
        if (control.value.isMandatory === true) {
          orderServiceMandatory.push(control);
        } else {
          orderServiceOptional.push(control);
        }
      } else {
        if (control.value.isMandatory === true) {
          sampleServiceMandatory.push(control);
        } else {
          sampleServiceOptional.push(control);
        }
      }
    }
    return sampleServiceMandatory
      .concat(sampleServiceOptional)
      .concat(orderServiceMandatory)
      .concat(orderServiceOptional);
  }

  public getTotalNoOfSamples(): number {
    return this.currentOrder?.samples?.length;
  }

  public getTotalNoOfAnalyses(): number {
    const samples = this.currentOrder.samples;
    let analysesCount = 0;
    samples?.forEach(x => {
      if (x.analyses) {
        analysesCount += x.analyses.length;
      }
    });
    return analysesCount;
  }

  public getShippingPrice(): number {
    return this.currentOrder.shipping?.orderShippingDetails?.estimatedPrice;
  }

  public getShippingService(): string {
    return this.currentOrder.shipping?.orderShippingDetails?.translationTag;
  }

  public reset() {
    this.currentOrder = {
      OrderCode: '',
      OrderName: '',
      AccountCode: '',
      OrderingServiceCode: '',
      QuotationCode: '',
      ContractCode: '',
      ReceivingLabCode: '',
      SampleServiceCode: '',
      samples: [],
      additionalFields: [],
      selfShipping: false,
      shipping: null,
      testParameterDictionary: null,
      CreatedBy: '',
      productMatrices: [],
      attachDocuments: [],
      newsamples: []
    };
  }

  public getTotalPriceOfSamplesInOrder(): number {
    const samples = this.currentOrder.samples;
    let total = 0;
    samples?.forEach(sample => {
      if (sample.analyses) {
        const price = sample.analyses.reduce((acc, y) => acc + (y.price*y.executionNumber), 0);
        const currencyCode = this.currentOrder.quotation.currencyCode + ' ';
        sample.price = this.formatterHelperService.formatCurrency(price, currencyCode);
        sample.priceValue = price;
        total += price;
      }
    });
    return total;
  }

  public atLeastOneAnalysisAdded(): boolean {
    return this.currentOrder.samples === undefined ||
      this.currentOrder.samples.every(sample => sample.analyses && sample.analyses.length > 0);
  }

  getConfiguredPONumber(callback: () => void) {
    this.labConfigQryClient.getDefaultPurchaseOrder(this.currentOrder.account.code).toPromise().then(x => {
      this.currentOrder.defaultPONumber = x?.poNumber;
      callback();
    }).catch(errorResponse => {
      if (errorResponse.status === 404) {
        callback();
      }
    });
    this.currentOrder.initialLoadOrderDetails = false;
    this.currentOrder.initialLoadReviewAndConfirm = false;
  }
}
